import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  AdornedInput,
  Adornment,
  DatePicker,
  DatePickerInput,
  DropdownSelect,
  IconCalendar,
  IconClock,
  Input,
  useToast,
} from '@zillow/constellation';
import { Box, Button, Divider, Heading, Modal, Spinner, Text } from '@zillow/constellation-10';
import {
  useTourSchedulingWebApiHandler_rescheduleTourByIrene,
  useZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene,
} from '@zg-rentals/ts-z-calendar-client-react-query';

import EventDetails from '../EventDetails';
import UnmaskedToast from '../UnmaskedToast';
import type { RescheduleTourModalTriggers } from '../../constants';
import {
  type ClickStreamUser,
  emitRescheduleTourModalImpresssionEvent,
} from '../../constants/TouringClickStreamEvents';
import {
  convert12HourTimeToLocalTime,
  convertDateToLocalDate,
  convertLocalDateTimeToDate,
  formatTourTimeRange,
  getToastDisplayDate,
  getTourDateAndTimeRange,
} from '../../utils/formatTime';

import type Constellation from '@zillow/constellation';
type DatePickerMetaProps = Constellation.DatePickerMetaProps;
type ComboboxInputProps = Constellation.ComboboxInputProps;

type RescheduleTourModalProps = {
  clickStreamUser?: ClickStreamUser;
  listingAlias: string;
  tourId: string;
  tourTitle: string;
  triggerSource: RescheduleTourModalTriggers;
  onClose: () => void;
  onRescheduleSuccess?: (startMs?: number) => void;
};

const DATE_FORMAT = 'EEE, LLL d yyyy';

const formatDateString = (date: Date) => {
  return format(date, DATE_FORMAT);
};

const getStartTimeFromTimeSlot = (timeSlot: string): string => {
  const [startTime, endTime] = timeSlot.split('-');

  if (startTime.includes('am') || startTime.includes('pm')) {
    return startTime;
  }

  const meridiem = endTime.slice(-2);
  return `${startTime}${meridiem}`;
};

const RescheduleTourModal = ({
  clickStreamUser,
  listingAlias,
  tourId,
  tourTitle,
  triggerSource,
  onClose,
  onRescheduleSuccess,
}: RescheduleTourModalProps) => {
  const [isCalendarInputFocused, setIsCalendarInputFocused] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTimeSlot, setRescheduleTimeSlot] = useState('');
  const { enqueueToast } = useToast();

  useEffect(() => {
    if (clickStreamUser && triggerSource) {
      emitRescheduleTourModalImpresssionEvent(clickStreamUser, triggerSource);
    }
  }, [clickStreamUser, triggerSource]);

  const {
    data: getRescheduleTimesRes,
    isLoading: isGetRescheduleTimesLoading,
    isSuccess: isGetRescheduleTimesSuccess,
  } = useZCalendarWebApiHandler_getAvailableTimesForRescheduleByIrene({
    listingAlias,
    tourId,
  });

  const { mutateAsync: rescheduleTour, isPending: isRescheduleTourLoading } =
    useTourSchedulingWebApiHandler_rescheduleTourByIrene();

  const datePickerInputValue = useMemo(() => {
    if (isCalendarInputFocused || !rescheduleDate) {
      return rescheduleDate;
    }
    return format(new Date(rescheduleDate), 'EEE, LLL d');
  }, [rescheduleDate, isCalendarInputFocused]);

  const rescheduleTimeSlotsByDate = useMemo(() => {
    if (!getRescheduleTimesRes?.availableTimeSlotsByDate) {
      return null;
    }

    const timeSlotsByDate = Object.values(getRescheduleTimesRes.availableTimeSlotsByDate).reduce<
      Record<string, Array<string>>
    >((acc, availableTimeSlots) => {
      availableTimeSlots.forEach((availableTimeSlot) => {
        const dateKey = formatDateString(convertLocalDateTimeToDate(availableTimeSlot.startTime));
        const formattedTourInterval = formatTourTimeRange(availableTimeSlot.startTime, availableTimeSlot.endTime);

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(formattedTourInterval);
      });
      return acc;
    }, {});

    return timeSlotsByDate;
  }, [getRescheduleTimesRes?.availableTimeSlotsByDate]);

  useEffect(() => {
    if (isGetRescheduleTimesSuccess && getRescheduleTimesRes) {
      const { startDateTime, endDateTime } = getRescheduleTimesRes;
      const tourDate = formatDateString(convertLocalDateTimeToDate(startDateTime));
      const tourTimeSlot = formatTourTimeRange(startDateTime, endDateTime);
      setRescheduleDate(tourDate);
      setRescheduleTimeSlot(tourTimeSlot);
    }
  }, [isGetRescheduleTimesSuccess, getRescheduleTimesRes]);

  const handleChangeDate = (value: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }

    setRescheduleDate(value);
    setRescheduleTimeSlot('');
  };

  const isDateDisabled = (date: Date) => {
    if (!rescheduleTimeSlotsByDate) {
      return true;
    }
    const dateKey = formatDateString(date);
    return !(dateKey in rescheduleTimeSlotsByDate);
  };

  const handleSelectTime = (option: { value: string }) => {
    setRescheduleTimeSlot(option.value);
  };

  const isRescheduleButtonDisabled = () => {
    if (
      !rescheduleDate ||
      !rescheduleTimeSlot ||
      isRescheduleTourLoading ||
      isGetRescheduleTimesLoading ||
      !getRescheduleTimesRes
    ) {
      return true;
    }

    const { startDateTime, endDateTime } = getRescheduleTimesRes;
    const originalTourDate = formatDateString(convertLocalDateTimeToDate(startDateTime));
    const originalTourTimeSlot = formatTourTimeRange(startDateTime, endDateTime);

    const isDateUnchanged = rescheduleDate === originalTourDate;
    const isTimeUnchanged = rescheduleTimeSlot === originalTourTimeSlot;
    return isDateUnchanged && isTimeUnchanged;
  };

  const handleRescheduleTour = () => {
    const startTimeString = getStartTimeFromTimeSlot(rescheduleTimeSlot);
    const localDate = convertDateToLocalDate(new Date(rescheduleDate));
    const localTime = convert12HourTimeToLocalTime(startTimeString);
    const startTime = { date: localDate, time: localTime };

    return rescheduleTour({
      postData: {
        listingAlias,
        tourId,
        startTime,
      },
    })
      .then((data) => {
        const tourDateAndTime = getToastDisplayDate(convertLocalDateTimeToDate(startTime));
        const successMessage = `The tour was rescheduled to ${tourDateAndTime}.`;
        enqueueToast(<UnmaskedToast appearance="success">{successMessage}</UnmaskedToast>);
        return onRescheduleSuccess?.(data.startMs);
      })
      .catch(() => {
        enqueueToast(
          <UnmaskedToast appearance="critical">
            The tour couldn&apos;t be rescheduled. Try again in a few moments.
          </UnmaskedToast>,
        );
      })
      .finally(() => {
        onClose();
      });
  };

  const modalBody =
    isGetRescheduleTimesLoading || !rescheduleTimeSlotsByDate || !getRescheduleTimesRes ? (
      <Box css={{ display: 'flex', justifyContent: 'center', padding: 'looser' }}>
        <Spinner size="xl" />
      </Box>
    ) : (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        <EventDetails
          bookedByName={getRescheduleTimesRes.bookedByName}
          eventDateAndTime={getTourDateAndTimeRange(
            getRescheduleTimesRes.startDateTime,
            getRescheduleTimesRes.endDateTime,
          )}
          listingStreet={getRescheduleTimesRes.listingStreet || ''}
          photoUrl={getRescheduleTimesRes?.listingPhoto?.displayableMediumUrl}
          title={tourTitle}
          tourStatus={getRescheduleTimesRes.tourStatus}
        />
        <Divider />
        <Text>
          Pick a new date/time to reschedule. This will book the tour, so make sure you&apos;ve confirmed this time
          works for the renter.
        </Text>
        <AdornedInput
          onFocus={() => setIsCalendarInputFocused(true)}
          input={
            <DatePickerInput
              value={datePickerInputValue}
              onBlur={() => setIsCalendarInputFocused(false)}
              onChange={handleChangeDate}
              datepicker={<DatePicker dateFormat={DATE_FORMAT} disabledDates={isDateDisabled} />}
            />
          }
          rightAdornment={
            <Adornment>
              <IconCalendar size="xs" />
            </Adornment>
          }
        />
        <DropdownSelect
          data-testid="RescheduleTourModal-TimeSlotDropdown"
          renderInput={(renderProps: ComboboxInputProps) => {
            return (
              <AdornedInput
                input={<Input placeholder="Select a time" {...renderProps} />}
                rightAdornment={
                  <Adornment>
                    <IconClock size="xs" />
                  </Adornment>
                }
              />
            );
          }}
          options={rescheduleTimeSlotsByDate[rescheduleDate] || []}
          onOptionSelect={handleSelectTime}
          value={rescheduleTimeSlot || ''}
        />
      </Box>
    );

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={3}>Reschedule tour</Heading>}
      body={modalBody}
      footer={
        <Button
          data-testid="RescheduleTourModal-RescheduleBtn"
          disabled={isRescheduleButtonDisabled()}
          onClick={handleRescheduleTour}
          emphasis="filled"
          fluid
        >
          Reschedule tour
        </Button>
      }
      size="xs"
    />
  );
};

export default RescheduleTourModal;
